import { useEffect, useState } from 'react'
import { errorMessage } from '../../api/client'
import { Dialog } from '../../components/Dialog'
import { Icon } from '../../components/Icon'
import { getMeta, metaKeys, setMeta } from '../../db/meta'
import { installPlatform } from '../install/installGate'
import { isNative, nativePlatform } from '../../native/native'
import { enableNativePush, nativePushState } from '../../native/nativePush'
import { currentSubscription, enablePush, pushSupport } from '../../push/push'
import { useSession } from '../../state/session'
import { toast } from '../../state/toasts'

/** "Not now" hides the question for this long. */
export const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000
/** Let the app draw its conversations before asking. */
const DELAY_MS = 1500

export type PushPromptKind = 'ask' | 'blocked'

/**
 * Whether to ask: push is set up on the server and works in this browser, this device gets no notifications, and the person has not said
 * "Not now" in the last week. 'blocked' when the browser was told no, which only its settings can undo.
 */
export function pushPromptKind(input: {
  configured: boolean
  support: ReturnType<typeof pushSupport>
  permission: NotificationPermission
  subscribed: boolean
  snoozedAt: number | undefined
  now: number
}): PushPromptKind | null {
  if (!input.configured || input.support !== 'supported') return null
  if (input.snoozedAt !== undefined && input.now - input.snoozedAt < SNOOZE_MS) return null
  if (input.permission === 'denied') return 'blocked'
  if (input.permission === 'granted' && input.subscribed) return null
  return 'ask'
}

function blockedHelp(): string {
  // The app, not a browser: its own entry in the phone's settings.
  if (nativePlatform() === 'ios') return 'Open the Settings app, tap Apps, then BOSS Chat, then Notifications, and turn on Allow Notifications.'
  if (nativePlatform() === 'android') return 'Open Settings, tap Apps, then BOSS Chat, then Notifications, and turn them on.'
  switch (installPlatform()) {
    case 'ios':
      return 'Open the Settings app, tap Notifications, then BOSS Chat, and turn on Allow Notifications.'
    case 'android':
      return 'Press and hold the BOSS Chat icon, tap App info, then Notifications, and turn them on.'
    default:
      return "Click the icon beside this page's address, allow Notifications, then reload the page."
  }
}

/** Asks, once the app has loaded, to turn on notifications on this device when they are off. */
export function PushPrompt() {
  const vapidPublicKey = useSession((s) => s.me?.vapidPublicKey ?? null)
  const nativePush = useSession((s) => s.me?.nativePush === true)
  const native = isNative()
  const configured = native ? nativePush : Boolean(vapidPublicKey)
  const [kind, setKind] = useState<PushPromptKind | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!configured) return
    let cancelled = false
    const timer = window.setTimeout(async () => {
      if (native) {
        // In the app the phone's own permission decides, and the token goes to FCM (native app spec, D10).
        const [state, snoozedAt] = await Promise.all([nativePushState().catch(() => 'on' as const), getMeta<number>(metaKeys.pushPromptSnoozedAt)])
        if (cancelled) return
        const snoozed = snoozedAt !== undefined && Date.now() - snoozedAt < SNOOZE_MS
        setKind(snoozed || state === 'on' ? null : state === 'denied' ? 'blocked' : 'ask')
        return
      }
      const support = pushSupport()
      if (support !== 'supported') return
      const [subscription, snoozedAt] = await Promise.all([currentSubscription().catch(() => null), getMeta<number>(metaKeys.pushPromptSnoozedAt)])
      if (cancelled) return
      setKind(pushPromptKind({ configured: true, support, permission: Notification.permission, subscribed: Boolean(subscription), snoozedAt, now: Date.now() }))
    }, DELAY_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [configured, native, vapidPublicKey])

  if (!kind || !configured) return null

  const snooze = () => {
    // Hidden once "Not now" is stored, so leaving straight away still counts.
    void setMeta(metaKeys.pushPromptSnoozedAt, Date.now())
      .catch(() => undefined)
      .finally(() => setKind(null))
  }

  const turnOn = async () => {
    setBusy(true)
    try {
      const result = native ? await enableNativePush() : await enablePush(vapidPublicKey!)
      if (result === 'enabled') {
        toast('Notifications are on')
        setKind(null)
      } else if (result === 'denied') {
        setKind('blocked')
      } else {
        setKind(null)
      }
    } catch (error) {
      toast(`Couldn't turn on notifications. ${errorMessage(error)}`, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={snooze} title={kind === 'ask' ? 'Turn on notifications?' : 'Notifications are blocked'} className="small-dialog push-prompt" closeLabel="Not now">
      <div className="dialog-body">
        <Icon name="bell" />
        {kind === 'ask' ? (
          <p>Get told about direct messages and mentions when BOSS Chat is closed. You can change this any time in Settings.</p>
        ) : (
          <p>This device won't show BOSS Chat notifications. {blockedHelp()}</p>
        )}
      </div>
      <div className="dialog-actions">
        <button type="button" className="button" onClick={snooze}>
          Not now
        </button>
        {kind === 'ask' ? (
          <button type="button" className="button primary" data-autofocus disabled={busy} onClick={() => void turnOn()}>
            Turn on
          </button>
        ) : (
          <button type="button" className="button primary" data-autofocus onClick={() => setKind(null)}>
            OK
          </button>
        )}
      </div>
    </Dialog>
  )
}
